import React, { useContext, useMemo } from "react";
import { flowRight, uniq } from "lodash";
import { useParams, useHistory } from "react-router-dom";
import { CardNotification, Link, Text } from "@foris/avocado-suite";
import { IParams } from "@models/IParams";
import { BookingContext } from "../../context/BookingContext";
import { Types } from "../../context/request.reducer";
import { Types as TypesSearch } from "../../context/search.reducer";
import css from "./requestErrors.module.scss";
import { Interval, ClassroomBookingValidationErrorUnion, SessionAssignment } from "@models/ISchema";
import { useTranslation } from "react-i18next";

const daysTranslations = {
  MONDAY: "Lun",
  TUESDAY: "Mar",
  WEDNESDAY: "Mie",
  THURSDAY: "Jue",
  FRIDAY: "Vie",
  SATURDAY: "Sab",
  SUNDAY: "Dom",
};

const Errors = () => {
  const { origin, scenario, workspace }: IParams = useParams();
  const { state, dispatch } = useContext(BookingContext);
  const history = useHistory();
  const errors = state?.request.errors;
  const urlContext = `${workspace}/${scenario}/${origin}`;
  const routePath = `/booking/search/${urlContext}`;
  const { t } = useTranslation();

  const isOnlyHolidayError = useMemo(() => {
    if (errors && errors.length) {
      return errors.every(error => error.__typename === "InvalidSessionInHoliday");
    }

    return false;
  }, [errors]);

  const getAssignmentsFromErrors = (errors: ClassroomBookingValidationErrorUnion[]) => {
    const assignments = [];
    errors.forEach(error => {
      if ("sessions" in error) {
        error.sessions.forEach(({ assignment }) => assignments.push(assignment));
      }
    });
    return assignments;
  };

  const getErrorsFromAssignments = (assignments: SessionAssignment[]) => {
    const errorsInfo = assignments.reduce<{ [key: string]: string[] }>(
      (error, { blockRange, intervals }) => {
        const format = (time: string) => {
          const [hour, min] = time.split(":");
          return `${hour}:${min}`;
        };

        const day = daysTranslations[blockRange?.start?.day];
        const startTime = format(blockRange?.start?.startingTime);
        const endTime = format(blockRange?.end?.endingTime);
        const time = `${startTime} - ${endTime}`;
        const dayAndTime = `${day}, ${time}`;

        const weeks = intervals.map((week: Interval) => week.name);
        error[dayAndTime] = dayAndTime in error ? [...error[dayAndTime], ...weeks] : [...weeks];

        return error;
      },
      {},
    );
    return Object.entries(errorsInfo).map(([date, weeks]) => [date, uniq(weeks).join(", ")]);
  };

  if (errors && errors.length) {
    const errorsBlock = errors.map((error, index) => {
      switch (error.__typename) {
        case "InvalidDate":
          return (
            <Text type="sm" key={index}>
              {t("booking.booking-request.notifications.invalid-date.description")}
            </Text>
          );
        case "InvalidSessionInHoliday":
          return (
            <Text type="sm" key={index}>
              {t("booking.booking-request.notifications.invalid-session-in-holiday.description")}
            </Text>
          );
        case "InvalidInput":
          return (
            <Text type="sm" key={index}>
              {error.code === "INTERVAL_NOT_FOUND"
                ? t("booking.booking-request.notifications.interval-not-found.description")
                : ""}
              {error.code === "INVALID_CLASSROOM"
                ? t("booking.booking-request.notifications.invalid-classroom.description")
                : ""}
            </Text>
          );

        case "ClassroomClash":
          const classroomClashErrors = flowRight(
            getErrorsFromAssignments,
            getAssignmentsFromErrors,
          )(errors);

          return (
            <div>
              <Text type="sm" key={index}>
                {t("booking.booking-request.notifications.clash-error.description")}
              </Text>
              <br />
              {classroomClashErrors.length &&
                classroomClashErrors.map(([date, weeks], idx: number) => {
                  return (
                    <Text key={idx} type="sm">
                      <strong>{t("booking.booking-request.notifications.clash-error.day")} </strong>{" "}
                      {date}{" "}
                      <strong>
                        {" "}
                        {t("booking.booking-request.notifications.clash-error.weeks")}{" "}
                      </strong>{" "}
                      {weeks}
                    </Text>
                  );
                })}
            </div>
          );
        default:
          return null;
      }
    });

    return (
      <CardNotification className={css.requestErrors} title="Error" state="error" outlined>
        <div className={css.requestErrors_content}>
          {errorsBlock}
          {!isOnlyHolidayError && (
            <Link
              iconRight="external-link"
              onClick={() => {
                dispatch({ type: Types.ClearRequest, payload: null });
                dispatch({ type: TypesSearch.ClearSearch, payload: null });
                history.push(routePath);
              }}
            >
              {t("booking.booking-request.notifications.hard-error.description-link")}
            </Link>
          )}
        </div>
      </CardNotification>
    );
  }

  return null;
};

export default Errors;
